"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Location = void 0;
const class_validator_1 = require("class-validator");
const moment = require("moment-timezone");
const cloneModelObject_1 = require("../util/cloneModelObject");
const errorTypes_1 = require("../helpers/errorTypes");
const distanceBetweenTwoLocations_1 = require("../../../helpers/distanceBetweenTwoLocations");
const currentUnixTimestamp_1 = require("../util/currentUnixTimestamp");
const constants_1 = require("../../../helpers/constants");
const locations_1 = require("./customValidators/locations");
const time_1 = require("../../../helpers/time");
class Location {
    constructor() {
        this.operationalDayRolloverTime = "0630";
    }
    get isOpen() {
        const now = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { openingTime, closingTime } = this.getOpeningTimesForDate(now);
        return now >= openingTime && now <= closingTime;
    }
    get currentTime() {
        return moment.tz(this.timezone.timeZoneId).unix();
    }
    get isOnline() {
        const { availableForPickup, availableForDelivery } = this.onlineStatus;
        return availableForDelivery || availableForPickup;
    }
    get weekdayText() {
        var _a, _b;
        const openingHours = ((_b = (_a = this.googlePlaces) === null || _a === void 0 ? void 0 : _a.openingHours) === null || _b === void 0 ? void 0 : _b.periods) || this.openingHours;
        if (!Array.isArray(openingHours)) {
            return [];
        }
        const result = constants_1.daysOfWeekMap.map(day => {
            // @ts-ignore
            const operationalPeriod = this.openingHours.find((period) => {
                var _a;
                if ("open" in period) {
                    return ((_a = period.open) === null || _a === void 0 ? void 0 : _a.day) === day[0];
                }
                return false;
            });
            if (!operationalPeriod) {
                return `${day[1]}: Closed`;
            }
            const openTime = moment(operationalPeriod.open.time, "hmm").format("HH:mm A");
            const closeTime = moment(operationalPeriod.close.time, "hmm").format("HH:mm A");
            return `${day[1]}: ${openTime} - ${closeTime}`;
        });
        return result;
    }
    static unserialize(location) {
        const newLocation = new this();
        newLocation.title = location.title;
        // craft seems to sometimes send ids as strings
        newLocation.id = location.id.toString();
        newLocation.intId = parseInt(newLocation.id, 10);
        newLocation.slug = location.slug;
        newLocation.address = location.address;
        newLocation.googlePlaces = location.googlePlaces;
        newLocation.openingHours = location.openingHours || [];
        newLocation.locationFeatures = location.locationFeatures;
        newLocation.timezone = location.timezone;
        newLocation.region = location.region;
        newLocation.onlineStatus = location.onlineStatus;
        newLocation.prontoId = location.prontoId;
        newLocation.regionCode = location.regionCode;
        newLocation.abnNumber = location.abnNumber;
        return newLocation;
    }
    serialize() {
        return {
            title: this.title,
            id: this.id,
            slug: this.slug,
            address: this.address,
            googlePlaces: this.googlePlaces,
            openingHours: this.openingHours,
            locationFeatures: this.locationFeatures,
            timezone: this.timezone,
            region: this.region,
            onlineStatus: this.onlineStatus,
            prontoId: this.prontoId,
            regionCode: this.regionCode,
            abnNumber: this.abnNumber
        };
    }
    filterSensitiveData() {
        return (0, cloneModelObject_1.cloneModelObject)(this);
    }
    /**
     * Validate model fields.
     */
    async validate() {
        const validated = await (0, class_validator_1.validate)(this);
        if (validated.length > 0) {
            throw new errorTypes_1.ValidationError(validated);
        }
        return true;
    }
    // TODO lat and long should be moved out of address field into a geolocation (lowercase) object
    // fix when changed - see NextLocation model for overriding method
    getLocationsDistance(latLng) {
        if (!this.address.lat || !this.address.lng)
            return null;
        return (0, distanceBetweenTwoLocations_1.distanceBetweenTwoLocations)(latLng, {
            lat: this.address.lat,
            lng: this.address.lng
        });
    }
    getOpeningTimesForDate(timestamp) {
        if (!this.googlePlaces || !this.googlePlaces.openingHours) {
            console.warn(`${this.title} has no google place info or opening hours set`);
            return {
                openingTime: null,
                closingTime: null,
                isOpenToday: false
            };
        }
        const { timezone: { timeZoneId } } = this;
        const operationalDay = this.getOperationalDay(timestamp);
        const momentDate = moment(operationalDay);
        const dayOfWeek = momentDate.day();
        const googlePlacesPeriod = this.googlePlaces.openingHours.periods.find(period => period.open.day === dayOfWeek);
        // if period for day of week does not exist on places obj store is closed
        if (googlePlacesPeriod === undefined) {
            return {
                openingTime: null,
                closingTime: null,
                isOpenToday: false
            };
        }
        const { open: { time: openTime }, close: { time: closingTime } } = googlePlacesPeriod;
        const momentOpening = (0, time_1.convertIsoDateAndHoursToLocalMomentDate)(operationalDay, openTime, timeZoneId);
        const momentClosing = (0, time_1.convertIsoDateAndHoursToLocalMomentDate)(operationalDay, closingTime, timeZoneId);
        // If closing is before opening, closing must be past midnight, so we add an extra day
        if (momentClosing.isBefore(momentOpening)) {
            momentClosing.add(1, "d");
        }
        return {
            openingTime: momentOpening.unix(),
            closingTime: momentClosing.unix(),
            isOpenToday: true
        };
    }
    /**
     * Get the business hours of a given operational day whether it is open or
     * closed
     */
    getBusinessHoursForOperationalDay(operationalDay) {
        // defined return value for a closed day
        const closedDayValue = {
            openingTime: null,
            closingTime: null,
            isOpenToday: false
        };
        // ensure that opening hours are present
        // TODO: can remove this later once validation is brought into the
        //  retrieval of data from the cms
        if (!this.googlePlaces || !this.googlePlaces.openingHours) {
            console.warn(`${this.title} has no google place info or opening hours set`);
            return closedDayValue;
        }
        // get the current locations operational period data for the given day
        const momentDate = moment(operationalDay);
        const operationalPeriod = this._getOperationalPeriodForOperationDay(momentDate);
        // if period for day of week does not exist the location is closed
        if (operationalPeriod === null) {
            return closedDayValue;
        }
        // get military time of opening and closing for current day
        const { open: { time: openTime }, close: { time: closingTime } } = operationalPeriod;
        // convert times to a localized date
        const { timezone: { timeZoneId } } = this;
        const momentOpening = (0, time_1.convertIsoDateAndHoursToLocalMomentDate)(operationalDay, openTime, timeZoneId);
        const momentClosing = (0, time_1.convertIsoDateAndHoursToLocalMomentDate)(operationalDay, closingTime, timeZoneId);
        // If closing is before opening, closing must be past midnight, so we add an extra day
        if (momentClosing.isBefore(momentOpening)) {
            momentClosing.add(1, "d");
        }
        return {
            openingTime: momentOpening.unix(),
            closingTime: momentClosing.unix(),
            isOpenToday: true
        };
    }
    /**
     * Get the operational ISO date for a given timestamp. This will depend
     * whether the timestamp is between the operational hours of the location
     * and also factors in the roll over time for the operational day which
     * can be set via an environment on client or config variable on services like
     * "0600" - 6am.
     *
     * */
    getOperationalDay(timestamp, // UTC
    operationalDayRolloverTime = this.operationalDayRolloverTime) {
        var _a;
        // ensure we have a timezone
        const timezoneId = (_a = this.timezone) === null || _a === void 0 ? void 0 : _a.timeZoneId;
        // calculate the current local time and day of the week
        const operationalDay = moment.tz(moment.unix(timestamp), timezoneId);
        // check the given operating hour to compare against the rollover time
        const operatingHour = operationalDay.format("HHmm");
        // if opening time is on or after rollover than the operating day is
        // this day
        if (operatingHour >= operationalDayRolloverTime) {
            const operationalPeriod = this._getOperationalPeriodForOperationDay(operationalDay);
            // location is closed
            if (operationalPeriod === null) {
                return null;
            }
            else {
                return operationalDay.format(constants_1.isoDateFormat);
            }
        }
        // otherwise we check the previous day
        const previousOperatingDay = moment
            .tz(moment.unix(timestamp), timezoneId)
            .subtract(1, "d");
        const previousOperationalPeriod = this._getOperationalPeriodForOperationDay(previousOperatingDay);
        return previousOperationalPeriod !== null
            ? previousOperatingDay.format(constants_1.isoDateFormat)
            : null;
    }
    /**
     * Find the next operational day from the current given day
     */
    findNextOperationalDay(operationalDay) {
        // get the next day from the given one
        const nextDay = moment(operationalDay)
            .tz(this.timezone.timeZoneId)
            .add(1, "d");
        // check if it is operational
        if (this._dayIsOperational(nextDay)) {
            // it is operational so return it back as an ISO date
            return nextDay.format(constants_1.isoDateFormat);
        }
        else {
            // keep recursively looping to each next day until we can find one that is operational
            return this.findNextOperationalDay(nextDay.format(constants_1.isoDateFormat));
        }
    }
    /**
     * Check whether a given timestamp is on the same operational day as the current time
     */
    isOnSameOperationDay(timestamp, operationalDayTimestamp) {
        // operational day for current point in time
        const currentOperationalDay = this.getOperationalDay(operationalDayTimestamp);
        // operational day for time stamp passed
        const givenOperationalDay = this.getOperationalDay(timestamp);
        // simple string match is sufficient
        return currentOperationalDay === givenOperationalDay;
    }
    /**
     * Get the operational period for the given day
     * If closed then no operation period is given
     */
    _getOperationalPeriodForOperationDay(momentDay) {
        var _a, _b;
        // ensure we have opening hours
        // HACK: to support both location and retail location until consolidated
        const openingHours = ((_b = (_a = this.googlePlaces) === null || _a === void 0 ? void 0 : _a.openingHours) === null || _b === void 0 ? void 0 : _b.periods) || this.openingHours;
        const dayNumberOfWeek = momentDay.day();
        // get the operation information for this day
        // @ts-ignore
        const operationalPeriod = openingHours.find((period) => {
            var _a;
            if ("open" in period) {
                return ((_a = period.open) === null || _a === void 0 ? void 0 : _a.day) === dayNumberOfWeek;
            }
            // HACK: we don't handle this in next location as googlePlaces should be
            // populated
            return false;
        });
        return operationalPeriod ? operationalPeriod : null;
    }
    /**
     * Check a location is operation for the given day
     */
    _dayIsOperational(momentDay) {
        const operationalPeriod = this._getOperationalPeriodForOperationDay(momentDay);
        return !!operationalPeriod;
    }
}
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "title", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "id", void 0);
__decorate([
    (0, class_validator_1.IsNumber)()
], Location.prototype, "intId", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "slug", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "address", void 0);
__decorate([
    (0, locations_1.IsOpeningHoursValid)(),
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "googlePlaces", void 0);
__decorate([
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.ArrayNotEmpty)(),
    (0, locations_1.IsOpeningHoursValid)(),
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "openingHours", void 0);
__decorate([
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.IsOptional)()
], Location.prototype, "locationFeatures", void 0);
__decorate([
    (0, locations_1.IsTimezoneValid)(),
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "timezone", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], Location.prototype, "region", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsString)()
], Location.prototype, "prontoId", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsString)()
], Location.prototype, "regionCode", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsString)()
], Location.prototype, "abnNumber", void 0);
exports.Location = Location;
//# sourceMappingURL=Location.js.map